import { bodyCopyBold, Button, Divider, ThresholdPill } from '@aon/aon-tws-common-ui';
import React, { useRef } from 'react';
import HtmlParser from 'html-react-parser';
import _ from 'lodash';
import { useParams } from 'react-router';
import styled from 'styled-components';
import ContentConstants from '../../../contentConstants';
import { deleteDimension as storeDeleteDimension, removeSurveyQuestionsFromDimension } from '../../../store/SurveyDimensionsSlice';
import { getAllSurveyDimensions } from '../../../utilities/DimensionHelper';
import useCultures from '../../../utilities/hooks/useCultures';
import useSurvey from '../../../utilities/hooks/useSurvey';
import useSurveyDimensions from '../../../utilities/hooks/useSurveyDimensions';
import { CardLayout, Section, WarningModal } from '../../common';
import { BodyCopyBold } from '../../common/styles/globalStyles';
import CustomDimensionModal from './CustomDimensionModal';
import ManageSurveyDimensionsTable from './ManageSurveyDimensionsTable';
import MoveQuestionsModal from './MoveQuestionsModal';
import CopyQuestionsModal from './CopyQuestionsModal';
import useSurveyDimensionManagement, { ManageSurveyDimensionModals } from '../../../utilities/hooks/useSurveyDimensionManagement';
import { useAppDispatch } from '../../../appStore';
import { ISurveyDimension, QuestionType } from '../../../models';

export interface IManageSurveyDimensionsProps {
	pageTitle: string;
	additionalHeaderContent?: JSX.Element;
	footerContent?: JSX.Element;
}

const WarningModalContentWrapper = styled('div')`
	strong {
		${bodyCopyBold};
	}
`;

const ManageSurveyDimensions = ({ pageTitle, additionalHeaderContent, footerContent }: IManageSurveyDimensionsProps) => {
	const { surveyId } = useParams<{ surveyId: string }>();
	const parsedSurveyId = parseInt(surveyId, 10);
	const { isLoadingSurveyDimensions, surveyDimensions, isSavingSurveyDimension, validationErrors } = useSurveyDimensions(parsedSurveyId);
	const { culturesLoading, availableCultures } = useCultures();
	const { isLoading: isSurveyLoading, surveyEditModel } = useSurvey(parsedSurveyId);
	const dispatch = useAppDispatch();

	const questions = surveyEditModel.questions.filter((q) => q.surveyQuestionId !== undefined && q.questionType !== QuestionType.Hierarchy);
	const activeSurveyQuestions = questions.filter((q) => q.isActive && !q.isDemographic);
	const surveyDimensionsWithQuestions = getAllSurveyDimensions(surveyDimensions, questions);
	const mostRecentSurveyDimensions = useRef(surveyDimensions);
	const mostRecentSurveyEditModel = useRef(surveyEditModel);

	const {
		state,
		updateDimensions,
		createDimension,
		editDimension,
		deleteDimension,
		setSelectedDimension,
		toggleQuestionSelection,
		toggleAllQuestionsDimensionSelection,
		resetDimensionQuestionSelection,
		moveQuestions,
		copyQuestions,
		removeQuestions,
		closeModal,
	} = useSurveyDimensionManagement(surveyDimensionsWithQuestions);

	if (surveyDimensions != mostRecentSurveyDimensions.current || surveyEditModel != mostRecentSurveyEditModel.current) {
		mostRecentSurveyDimensions.current = surveyDimensions;
		mostRecentSurveyEditModel.current = surveyEditModel;
		updateDimensions(surveyDimensionsWithQuestions);
	}

	const handleOnModalClose = () => {
		closeModal();
	};

	const handleOnModalSaveWithoutClose = (savedDimension: ISurveyDimension) => {
		setSelectedDimension(savedDimension);
	};
	const checkFollowUpOption = () => {
		const followQuestions = questions.filter((q) => !!q.parentQuestionId);
		return (
			state.selectedSurveyDimension &&
			followQuestions &&
			followQuestions.some((q) => q.responseOptions && q.responseOptions.indexOf(state.selectedSurveyDimension.name) >= 0)
		);
	};
	const isFollowUpQuestionOption = checkFollowUpOption() && pageTitle === ContentConstants.SECTION_HEADERS_MANAGE_DIMENSIONS;

	return (
		<CardLayout
			loading={isLoadingSurveyDimensions || culturesLoading || isSurveyLoading}
			title={pageTitle}
			header={additionalHeaderContent}
		>
			<Section id={'survey-dimensions'} sectionHeader={ContentConstants.TITLE_MANAGE_SURVEY_DIMENSIONS}>
				<div className='row'>
					<div className='col-lg-9 col-sm-12'>
						Survey dimension organize questions under broad categories for this survey. Selected scored questions can only be listed{' '}
						<BodyCopyBold>under 1 scored dimension at a time.</BodyCopyBold>
					</div>
					<div className='col-lg-3 col-sm-12'>
						<Button onClick={createDimension}>Create Dimension</Button>
					</div>
				</div>
				<div className='row mt-5'>
					<div className='col-12' data-testid='questionCount'>
						Total selected survey questions <ThresholdPill count={activeSurveyQuestions.length} />
					</div>
				</div>
				<div className='row mt-5'>
					<ManageSurveyDimensionsTable
						dimensions={surveyDimensionsWithQuestions}
						onEdit={editDimension}
						onMoveQuestions={moveQuestions}
						onCopyQuestions={copyQuestions}
						onRemoveQuestions={removeQuestions}
						onDelete={deleteDimension}
						selectedQuestionsMap={state.selectedSurveyDimensionQuestions}
						onToggleSingleQuestionSelection={toggleQuestionSelection}
						onToggleAllQuestionsSelection={toggleAllQuestionsDimensionSelection}
					/>
				</div>
			</Section>
			<Divider type='dashed' />
			{footerContent}
			<CustomDimensionModal
				isFollowUpQuestionOption={isFollowUpQuestionOption}
				isOpen={state.currentModal === ManageSurveyDimensionModals.ManageDimension}
				surveyId={parsedSurveyId}
				dimensionToEdit={state.selectedSurveyDimension}
				defaultSurveyCultureCode={surveyEditModel.defaultCultureCode}
				supportedSurveyCultureCodes={surveyEditModel.supportedCultures}
				availableCultures={availableCultures}
				isSavingSurveyDimension={isSavingSurveyDimension}
				onModalClose={handleOnModalClose}
				onSaveWithoutClose={handleOnModalSaveWithoutClose}
				validationErrors={validationErrors}
			/>
			{state.selectedSurveyDimension && (
				<MoveQuestionsModal
					isOpen={state.currentModal === ManageSurveyDimensionModals.MoveQuestions}
					surveyId={parsedSurveyId}
					sourceSurveyDimensionId={state.selectedSurveyDimension.dimensionId}
					questionsToMove={state.selectedQuestionsForBulkAction}
					availableDimensions={state.availableDimensionsForBulkAction}
					isSaving={isSavingSurveyDimension}
					onCancel={closeModal}
					onSubmit={() => {
						resetDimensionQuestionSelection(state.selectedSurveyDimension);
						closeModal();
					}}
				/>
			)}
			<CopyQuestionsModal
				isOpen={state.currentModal === ManageSurveyDimensionModals.CopyQuestions}
				surveyId={parsedSurveyId}
				questionsToCopy={state.selectedQuestionsForBulkAction}
				availableDimensions={state.availableDimensionsForBulkAction}
				isSaving={isSavingSurveyDimension}
				onCancel={closeModal}
				onSubmit={() => {
					resetDimensionQuestionSelection(state.selectedSurveyDimension);
					closeModal();
				}}
			/>
			{state.selectedSurveyDimension && (
				<WarningModal
					isOpen={state.currentModal === ManageSurveyDimensionModals.RemoveQuestions}
					contentHeadline={ContentConstants.WARNING_MODAL_REMOVE_DIMENSION_QUESTION_HEADLINE}
					contentBody={
						<WarningModalContentWrapper>
							{HtmlParser(
								ContentConstants.WARNING_MODAL_REMOVE_DIMENSION_QUESTION_BODY.replace(
									'{questionCount}',
									state.selectedQuestionsForBulkAction.length,
								),
							)}
						</WarningModalContentWrapper>
					}
					onClose={closeModal}
					onSubmit={async () => {
						await dispatch(
							removeSurveyQuestionsFromDimension({
								surveyId: parsedSurveyId,
								surveyDimensionId: state.selectedSurveyDimension.dimensionId,
								questionsToRemove: state.selectedQuestionsForBulkAction,
							}),
						);
						closeModal();
					}}
				/>
			)}
			{state.selectedSurveyDimension && (
				<WarningModal
					isOpen={state.currentModal === ManageSurveyDimensionModals.DeleteDimension}
					contentHeadline={ContentConstants.WARNING_MODAL_DELETE_DIMENSION_HEADLINE}
					contentBody={
						<WarningModalContentWrapper>
							{HtmlParser(
								isFollowUpQuestionOption
									? ContentConstants.WARNING_MODAL_DELETE_DIMENSION_NOT_ALLOWED
									: ContentConstants.WARNING_MODAL_DELETE_DIMENSION_BODY,
							)}
						</WarningModalContentWrapper>
					}
					onClose={closeModal}
					primaryButtonDisabled={isFollowUpQuestionOption}
					onSubmit={async () => {
						await dispatch(storeDeleteDimension({ surveyId: parsedSurveyId, dimensionId: state.selectedSurveyDimension.dimensionId! }));
						closeModal();
					}}
				/>
			)}
		</CardLayout>
	);
};

export default ManageSurveyDimensions;
